import {
    FeatureDescription,
    FeatureHeadline,
    FeatureImage,
    FeatureSection,
    Headline,
    HeroSection,
    PremiumIncludesBasic,
    Subtitle,
} from "./details-page.styles";
import { Col, Row } from "react-bootstrap";
import Card from "./components/card/card";
import BasicPackageIcon from "../../../../assets/images/landing-page/basic-package/basic-package-icon.webp";
import PremiumPackageIcon from "../../../../assets/images/landing-page/premium-package/premium-package-icon.webp";
import { useEffect } from "react";
import CTAButton from "../../../../components/cta-button/cta-button";
import useIsMobile from "../../../../hooks/useIsMobile";
import TabBar from "../../components/tab-bar/tab-bar";
import { useLocation } from "react-router-dom";
import Footer from "../../components/footer/footer";
import useCustomNavigate from "../../../../hooks/useCustomNavigate";
import { useContentArea } from "../../../../context/ContentAreaContext";
import PackageSection from "../../../../components/package-section/package-section";
import useContactUsModalStore from "../../../../stores/contactUsModalStore";

// Images
import BasicImage1 from "../../../../assets/images/landing-page/basic-package/basic_image_1.webp";
import BasicImage2 from "../../../../assets/images/landing-page/basic-package/basic_image_2.webp";
import BasicImage3 from "../../../../assets/images/landing-page/basic-package/basic_image_3.webp";
import BasicImage4 from "../../../../assets/images/landing-page/basic-package/basic_image_4.webp";
import BasicImage5 from "../../../../assets/images/landing-page/basic-package/basic_image_5.webp";
import BasicImage6 from "../../../../assets/images/landing-page/basic-package/basic_image_6.webp";

import PremiumImage1 from "../../../../assets/images/landing-page/premium-package/premium_image_1.webp";
import PremiumImage2 from "../../../../assets/images/landing-page/premium-package/premium_image_2.webp";
import PremiumImage3 from "../../../../assets/images/landing-page/premium-package/premium_image_3.webp";
import FAQSection, { getInvestorFAQItems } from "../../components/faq-section/faq-section";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import JsonLd from "../../../../components/json-ld/json-ld";

const DetailsPage: React.FC = () => {
    const isMobile = useIsMobile();
    const navigate = useCustomNavigate();
    const location = useLocation();
    const { contentAreaRef } = useContentArea();
    const { t } = useTranslation();

    // Stores
    const setIsContactUsModalVisible = useContactUsModalStore(state => state.setIsVisible);

    // Structured data
    const faqItems = useMemo(() => getInvestorFAQItems(t), [t]);

    // Effects
    useEffect(() => {
        const anchor = location.hash.replace("#", "");

        if (anchor) {
            const scrollToElement = () => {
                if (contentAreaRef.current) {

                    const targetElement = contentAreaRef.current.querySelector(`#${anchor}`);

                    if (targetElement) {
                        const offset = 65; // Adjust for the fixed header/navbar height
                        const contentAreaTop = contentAreaRef.current.getBoundingClientRect().top;
                        const elementPosition = targetElement.getBoundingClientRect().top - contentAreaTop;
                        const offsetPosition = elementPosition - offset;

                        // Smooth scroll within the content area
                        contentAreaRef.current.scrollTo({
                            top: contentAreaRef.current.scrollTop + offsetPosition,
                            behavior: "smooth",
                        });
                    }
                }
            };

            // Wait for DOM updates before scrolling
            setTimeout(scrollToElement, 200);
        }
    }, [contentAreaRef, location]);

    return (
        <>
            <Row>
                <Col xs={12} md={11} xxl={10} className="mx-auto">
                    <TabBar isDefaultSticky />

                    <HeroSection id="services">
                        <Headline>{t('details.hero.headline')}</Headline>
                        <Subtitle>{t('details.hero.subtitle')}</Subtitle>
                        <div className="mt-3">
                            <CTAButton
                                variant="ghost"
                                size="M"
                                label={t('details.hero.cta_button')}
                                icon="text_compare"
                                onClick={() => navigate("/details#packages")}
                                noPadding
                            />
                        </div>
                    </HeroSection>

                    <FeatureSection>
                        <Row>
                            <Col xs={12} md={3} className={isMobile ? "" : "text-start"}>
                                <div style={isMobile ? undefined : { marginTop: 42 }}>
                                    <FeatureImage src={BasicPackageIcon} alt="Basic Package" />
                                    <FeatureHeadline>{t('details.basic_package.headline')}</FeatureHeadline>
                                    <FeatureDescription>{t('details.basic_package.description')}</FeatureDescription>
                                </div>
                            </Col>

                            <Col xs={12} md={9}>
                                <Row className="align-items-stretch gx-3 gy-3">
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Basic"
                                            iconSrc={BasicImage1}
                                            title={t('details.basic_package.services.handover_support.title')}
                                            description={t('details.basic_package.services.handover_support.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Basic"
                                            iconSrc={BasicImage2}
                                            iconSize={90}
                                            title={t('details.basic_package.services.marketing_rental.title')}
                                            description={t('details.basic_package.services.marketing_rental.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Basic"
                                            iconSrc={BasicImage3}
                                            iconSize={90}
                                            title={t('details.basic_package.services.tenant_management.title')}
                                            description={t('details.basic_package.services.tenant_management.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Basic"
                                            iconSrc={BasicImage4}
                                            title={t('details.basic_package.services.inspections_maintenance.title')}
                                            description={t('details.basic_package.services.inspections_maintenance.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Basic"
                                            iconSrc={BasicImage5}
                                            iconSize={145}
                                            title={t('details.basic_package.services.landlord_portal.title')}
                                            description={t('details.basic_package.services.landlord_portal.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Basic"
                                            iconSrc={BasicImage6}
                                            iconSize={97}
                                            title={t('details.basic_package.services.rental_income_management.title')}
                                            description={t('details.basic_package.services.rental_income_management.description')}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </FeatureSection>

                    <FeatureSection>
                        <Row className={isMobile ? "" : "align-items-center"}>
                            <Col xs={12} md={3} className={isMobile ? "" : "text-start"}>
                                <FeatureImage src={PremiumPackageIcon} alt="Premium Package" />
                                <FeatureHeadline>{t('details.premium_package.headline')}</FeatureHeadline>
                                <FeatureDescription>{t('details.premium_package.description')}</FeatureDescription>
                            </Col>

                            <Col xs={12} md={9}>
                                <Row className="align-items-stretch gx-3 gy-3">
                                    <PremiumIncludesBasic>{t('details.premium_package.includes_basic')}</PremiumIncludesBasic>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Premium"
                                            iconSrc={PremiumImage1}
                                            iconSize={105}
                                            title={t('details.premium_package.services.service_charge_management.title')}
                                            description={t('details.premium_package.services.service_charge_management.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Premium"
                                            iconSrc={PremiumImage2}
                                            iconSize={100}
                                            title={t('details.premium_package.services.debt_collection.title')}
                                            description={t('details.premium_package.services.debt_collection.description')}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} xl={4}>
                                        <Card
                                            badgeText="Premium"
                                            iconSrc={PremiumImage3}
                                            iconSize={100}
                                            title={t('details.premium_package.services.warranty_maintenance_management.title')}
                                            description={t('details.premium_package.services.warranty_maintenance_management.description')}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </FeatureSection>

                    <PackageSection id="packages" withoutBackground />

                    {/* FAQ Section */}
                    <FAQSection id="faq" style={{ paddingBottom: 100 }} />

                </Col>
            </Row>

            <Footer />

            {/* Structured Data */}
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqItems.map(item => ({
                    "@type": "Question",
                    "name": item.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": item.answer
                    }
                }))
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Property Management",
                "provider": {
                    "@type": "Organization",
                    "name": "Managd Property Management LLC"
                },
                "areaServed": {
                    "@type": "City",
                    "name": "Dubai"
                },
                "hasOfferCatalog": {
                    "@type": "OfferCatalog",
                    "name": "Property Management Packages",
                    "itemListElement": [
                        {
                            "@type": "Offer",
                            "name": "Basic",
                            "description": t('details.basic_package.description'),
                            "itemOffered": {
                                "@type": "Service",
                                "name": t('details.basic_package.headline'),
                                "hasOfferCatalog": {
                                    "@type": "OfferCatalog",
                                    "itemListElement": [
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.basic_package.services.handover_support.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.basic_package.services.marketing_rental.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.basic_package.services.tenant_management.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.basic_package.services.inspections_maintenance.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.basic_package.services.landlord_portal.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.basic_package.services.rental_income_management.title') } }
                                    ]
                                }
                            }
                        },
                        {
                            "@type": "Offer",
                            "name": "Premium",
                            "description": t('details.premium_package.description'),
                            "itemOffered": {
                                "@type": "Service",
                                "name": t('details.premium_package.headline'),
                                "hasOfferCatalog": {
                                    "@type": "OfferCatalog",
                                    "itemListElement": [
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.premium_package.services.service_charge_management.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.premium_package.services.debt_collection.title') } },
                                        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": t('details.premium_package.services.warranty_maintenance_management.title') } }
                                    ]
                                }
                            }
                        }
                    ]
                }
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://www.getmanagd.com/home"
                    },
                    {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Details",
                        "item": "https://www.getmanagd.com/details"
                    }
                ]
            }} />
        </>
    );
};

export default DetailsPage;
